/**
 * Push Notifications Service
 * Handles browser push notifications for NC Help Hub app
 */

// Check if browser supports notifications
export const isNotificationSupported = () => {
  return 'Notification' in window;
};

// Get current notification permission status
export const getNotificationPermission = () => {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    return 'unsupported';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  // Request permission
  const permission = await Notification.requestPermission();
  return permission;
};

// Play notification sound
const playNotificationSound = () => {
  try {
    // Create a simple beep sound using Web Audio API
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    
    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.2);
  } catch (e) {
    // Audio API not supported or blocked by browser - silently ignore
    // This is expected on some browsers/devices with strict audio policies
    console.debug('Notification sound skipped:', e.message);
  }
};

// Show a browser notification
export const showNotification = (title, options = {}) => {
  if (!isNotificationSupported() || Notification.permission !== 'granted') {
    return null;
  }

  const defaultOptions = {
    icon: '/logo192.png', // App icon
    badge: '/logo192.png',
    vibrate: [200, 100, 200],
    requireInteraction: false,
    silent: false,
    ...options
  };

  try {
    const notification = new Notification(title, defaultOptions);
    
    // Play sound for notification
    playNotificationSound();
    
    // Auto close after 8 seconds if not clicked
    setTimeout(() => {
      notification.close();
    }, 8000);

    return notification;
  } catch (error) {
    console.error('Error showing notification:', error);
    return null;
  }
};

// Show chat message notification
export const showChatNotification = (senderName, message, onClick) => {
  const notification = showNotification(`New message from ${senderName}`, {
    body: message.length > 100 ? message.substring(0, 100) + '...' : message,
    tag: 'chat-message', // Prevents duplicate notifications
    renotify: true,
    data: { type: 'chat' }
  });

  if (notification && onClick) {
    notification.onclick = () => {
      window.focus();
      onClick();
      notification.close();
    };
  }

  return notification;
};

// Show booking notification
export const showBookingNotification = (title, message, onClick) => {
  const notification = showNotification(title, {
    body: message,
    tag: 'booking-' + Date.now(),
    renotify: true,
    data: { type: 'booking' }
  });

  if (notification && onClick) {
    notification.onclick = () => {
      window.focus();
      onClick();
      notification.close();
    };
  }

  return notification;
};

// Show general notification
export const showGeneralNotification = (title, message, onClick) => {
  const notification = showNotification(title, {
    body: message,
    tag: 'general-' + Date.now(),
    renotify: true
  });

  if (notification && onClick) {
    notification.onclick = () => {
      window.focus();
      onClick();
      notification.close();
    };
  }

  return notification;
};

export default {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  showNotification,
  showChatNotification,
  showBookingNotification,
  showGeneralNotification
};
